import { useCallback, useEffect, useMemo, useState } from "react";
import { registerSW } from "virtual:pwa-register";

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

export type PwaState = {
  standalone: boolean;
  installAvailable: boolean;
  ios: boolean;
  updateReady: boolean;
  offlineReady: boolean;
  installing: boolean;
  installed: boolean;
  install(): Promise<"accepted" | "dismissed" | "unavailable">;
  dismissInstall(): void;
  activateUpdate(canReload: boolean): Promise<boolean>;
  checkForUpdate(): Promise<void>;
};

let deferredPrompt: InstallPromptEvent | null = null;
const listeners = new Set<() => void>();

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault();
    deferredPrompt = event as InstallPromptEvent;
    for (const listener of listeners) listener();
  });
}

export function usePwa(): PwaState {
  const [installAvailable, setInstallAvailable] = useState(
    Boolean(deferredPrompt),
  );
  const [installed, setInstalled] = useState(false);
  const [updateReady, setUpdateReady] = useState(false);
  const [offlineReady, setOfflineReady] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [updateServiceWorker, setUpdateServiceWorker] = useState<
    ((reload?: boolean) => Promise<void>) | null
  >(null);
  const standalone =
    window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator &&
      (navigator as Navigator & { standalone?: boolean }).standalone === true);
  const ios =
    /iP(?:hone|ad|od)/.test(navigator.userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  useEffect(() => {
    const refresh = () => setInstallAvailable(Boolean(deferredPrompt));
    listeners.add(refresh);
    const onInstalled = () => {
      setInstalled(true);
      deferredPrompt = null;
      refresh();
    };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      listeners.delete(refresh);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (!("serviceWorker" in navigator)) return;
    const update = registerSW({
      immediate: true,
      onNeedRefresh() {
        setUpdateReady(true);
      },
      onOfflineReady() {
        setOfflineReady(true);
      },
      onRegisteredSW(_url, registration) {
        if (!registration) return;
        const check = () => void registration.update();
        window.addEventListener("focus", check);
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") check();
        });
        window.setInterval(check, 60 * 60 * 1000);
      },
      onRegisterError(error) {
        console.error("Service worker registration failed", error);
      },
    });
    setUpdateServiceWorker(() => update);
  }, []);

  useEffect(() => {
    const message = (event: MessageEvent) => {
      if (event.data?.type === "REQUEST_FOREGROUND_SYNC")
        window.dispatchEvent(new Event("10thingz:sync-requested"));
    };
    navigator.serviceWorker?.addEventListener("message", message);
    return () =>
      navigator.serviceWorker?.removeEventListener("message", message);
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt || standalone) return "unavailable" as const;
    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "dismissed")
        localStorage.setItem(
          "10thingz.install-dismissed-at",
          new Date().toISOString(),
        );
      deferredPrompt = null;
      setInstallAvailable(false);
      return choice.outcome;
    } finally {
      setInstalling(false);
    }
  }, [standalone]);

  const dismissInstall = useCallback(() => {
    localStorage.setItem(
      "10thingz.install-dismissed-at",
      new Date().toISOString(),
    );
    setInstallAvailable(false);
  }, []);

  const activateUpdate = useCallback(
    async (canReload: boolean) => {
      if (!canReload || !updateServiceWorker) return false;
      await updateServiceWorker(true);
      return true;
    },
    [updateServiceWorker],
  );

  const checkForUpdate = useCallback(async () => {
    const registration = await navigator.serviceWorker?.getRegistration("/");
    await registration?.update();
  }, []);

  return useMemo(
    () => ({
      standalone,
      installAvailable: installAvailable && !standalone,
      ios,
      updateReady,
      offlineReady,
      installing,
      installed,
      install,
      dismissInstall,
      activateUpdate,
      checkForUpdate,
    }),
    [
      standalone,
      installAvailable,
      ios,
      updateReady,
      offlineReady,
      installing,
      installed,
      install,
      dismissInstall,
      activateUpdate,
      checkForUpdate,
    ],
  );
}
